import type { NotificationIndicatorsRisk } from '@/domains/notification/notification.entity';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Estado de las notificaciones
 */
interface NotificationState {
  notifications: NotificationIndicatorsRisk | null;
  isLoaded: boolean;
  loading: boolean;
  error: string | null;
  toastsShown: boolean; // Flag para controlar si ya se mostraron los toasts
}

/**
 * Acciones del store de notificaciones
 */
interface NotificationActions {
  setNotifications: (notifications: NotificationIndicatorsRisk) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  markAsLoaded: () => void;
  markToastsAsShown: () => void;
  reset: () => void;
  resetData: () => void;
  getTotalNotifications: () => number;
  hasNotifications: () => boolean;
}

/**
 * Entidad completa del store (Estado + Acciones)
 */
interface NotificationStoreEntity
  extends NotificationState,
    NotificationActions {}

/**
 * Estado inicial del store
 */
const defaultNotificationState: NotificationState = {
  notifications: null,
  isLoaded: false,
  loading: false,
  error: null,
  toastsShown: false,
};

/**
 * Store de notificaciones usando Zustand
 * Maneja el estado global de las notificaciones del usuario
 * Incluye flag isLoaded para asegurar que la llamada se haga solo una vez
 * Persiste: toastsShown en localStorage para evitar mostrar toasts repetidos
 * NO persiste: datos de notificaciones (deben ser frescos en cada sesión)
 */
export const useNotificationStore = create<NotificationStoreEntity>()(
  persist(
    (set, get) => ({
      ...defaultNotificationState,

      /**
       * Establece las notificaciones y marca como cargado
       *
       * @param notifications - Datos de notificaciones del usuario
       */
      setNotifications: (notifications: NotificationIndicatorsRisk) =>
        set({
          notifications,
          loading: false,
          error: null,
          isLoaded: true,
        }),

      /**
       * Establece el estado de loading
       *
       * @param loading - Estado de carga
       */
      setLoading: (loading: boolean) => set({ loading }),

      /**
       * Establece un error y detiene el loading
       *
       * @param error - Mensaje de error o null para limpiar
       */
      setError: (error: string | null) =>
        set({
          error,
          loading: false,
        }),

      /**
       * Marca las notificaciones como cargadas
       */
      markAsLoaded: () => set({ isLoaded: true }),

      /**
       * Marca que los toasts ya fueron mostrados en esta sesión
       */
      markToastsAsShown: () => set({ toastsShown: true }),

      /**
       * Resetea el estado completo a valores por defecto
       * IMPORTANTE: También resetea toastsShown (usado en logout completo)
       */
      reset: () => set(defaultNotificationState),

      /**
       * Resetea solo los datos de notificaciones, mantiene toastsShown
       * Usado para forzar refresh de notificaciones sin mostrar toasts de nuevo
       */
      resetData: () =>
        set({
          notifications: null,
          isLoaded: false,
          loading: false,
          error: null,
          // toastsShown se mantiene como está
        }),

      /**
       * Obtiene el número total de notificaciones activas
       *
       * @returns Contador de notificaciones
       */
      getTotalNotifications: (): number => {
        const { notifications } = get();
        if (!notifications) return 0;
        return Object.values(notifications).filter(Boolean).length;
      },

      /**
       * Verifica si existen notificaciones activas
       *
       * @returns true si hay al menos una notificación
       */
      hasNotifications: (): boolean => {
        const { notifications } = get();
        if (!notifications) return false;
        return Object.values(notifications).some(Boolean);
      },
    }),
    {
      name: '@app/notifications',
      partialize: (state) => ({
        toastsShown: state.toastsShown,
      }),
    },
  ),
);
